import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useQuery } from '@tanstack/react-query';
import { getDashboardData } from '@/services/dashboardApi';
import { queryKeys } from '@/lib/queryKeys';
import PageLoading from '@/components/common/PageLoading';
import ContentApiErrorState, { ContentLoadError } from '@/components/common/ContentErrorState';
import EmptyState from '@/components/common/EmptyState';
import SectionCard from '@/components/common/SectionCard';

const statCards = [
  { key: 'completedProblems' as const, label: '已完成题目', suffix: '' },
  { key: 'totalSubmissions' as const, label: '总提交数', suffix: '' },
  { key: 'reviewDue' as const, label: '待复习', suffix: '' },
  { key: 'accuracy' as const, label: '正确率', suffix: '%' },
];

export default function DashboardPage() {
  const user = useAuthStore((state) => state.user);

  const {
    data,
    isLoading,
    isError,
    error: dashboardError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.dashboard.data(10),
    queryFn: () => getDashboardData(10),
    retry: 1,
  });

  if (isLoading) {
    return <PageLoading message='仪表盘加载中…' />;
  }

  if (isError) {
    return (
      <div className='max-w-[980px] mx-auto px-6 py-12'>
        <ContentApiErrorState error={dashboardError} onRetry={() => void refetch()} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className='max-w-[980px] mx-auto px-6 py-12'>
        <ContentLoadError onRetry={() => void refetch()} />
      </div>
    );
  }

  const { summary, reviewQueue } = data;

  return (
    <div className='max-w-[980px] mx-auto px-6 py-12'>
      <h1
        className='font-semibold text-apple-near-black mb-10'
        style={{ fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}
      >
        欢迎回来，{user?.name}
      </h1>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-10'>
        {statCards.map(({ key, label, suffix }) => (
          <div key={key} className='bg-white rounded-2xl p-6 shadow-card-sm'>
            <p className='text-apple-gray text-[0.8125rem] mb-2'>{label}</p>
            <p
              className='font-semibold text-apple-blue'
              style={{ fontSize: '2rem', lineHeight: 1 }}
            >
              {summary[key] ?? 0}
              {suffix}
            </p>
          </div>
        ))}
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
        <SectionCard title='待复习内容' subtitle='根据 SRS 复习队列推荐'>
          {reviewQueue.length > 0 ? (
            <div className='space-y-2'>
              {reviewQueue.map((item) => (
                <Link
                  key={item.problemId}
                  to={`/problems/${item.problemId}`}
                  className='block px-4 py-3 rounded-xl bg-apple-light-gray text-apple-near-black text-[0.9375rem] hover:bg-[#e8e8ed] transition-colors'
                >
                  {item.title}
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              title='暂无待复习内容'
              description='你当前没有到期复习，继续做题会自动生成后续复习计划。'
            />
          )}
        </SectionCard>

        <SectionCard title='快速开始' subtitle='常用入口'>
          <div className='space-y-3'>
            <Link to='/problems' className='btn btn-primary w-full justify-center'>
              开始练习
            </Link>
            <Link to='/learning' className='btn btn-dark w-full justify-center'>
              学习路径
            </Link>
            <Link to='/submissions' className='btn btn-secondary w-full justify-center'>
              查看提交记录
            </Link>
          </div>
        </SectionCard>
      </div>

      {summary.topErrors.length > 0 ? (
        <SectionCard title='常见错误标签' subtitle='最近提交中命中最多的错误类型' className='mt-5'>
          <div className='flex flex-wrap gap-2'>
            {summary.topErrors.map((item) => (
              <span
                key={item.tag}
                className='inline-flex px-3 py-1 rounded-full bg-apple-light-gray text-apple-near-black text-[0.8125rem]'
              >
                {item.tag} · {item.count}
              </span>
            ))}
          </div>
        </SectionCard>
      ) : null}

      <div className='mt-5'>
        <SectionCard title='学习任务' subtitle='今日任务概览'>
          <p className='text-apple-gray text-[0.9375rem]'>
            待复习：{summary.reviewDue} 项 · 待完成作业：{summary.assignmentsDue} 项
          </p>
        </SectionCard>
      </div>
    </div>
  );
}
